import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, Plus, Trash2, RefreshCw, ExternalLink, CheckCircle, Clock, Edit3 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const STATUS_OPTS = ['pending', 'in_progress', 'live', 'inactive'];

const STATUS_STYLES = {
  pending:     'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  in_progress: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  live:        'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  inactive:    'bg-slate-100 text-slate-500',
};

const errDetail = (err) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
  return err.message || 'Request failed';
};

export default function Web20ProfilesPage() {
  const [platforms, setPlatforms] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('profiles');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [editId, setEditId] = useState(null);

  // Create form
  const [platformId, setPlatformId] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [notes, setNotes] = useState('');

  // Edit inline state
  const [editData, setEditData] = useState({});

  const load = async () => {
    try {
      const [pRes, prRes, stRes] = await Promise.all([
        api.get('/web20-profiles/platforms'),
        api.get('/web20-profiles/'),
        api.get('/web20-profiles/stats'),
      ]);
      setPlatforms(pRes.data.platforms || []);
      setProfiles(prRes.data.profiles || []);
      setStats(stRes.data);
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!platformId) { setStatus('Error: Select a platform.'); return; }
    setLoading(true);
    setStatus('Creating profile...');
    try {
      await api.post('/web20-profiles/', {
        platform_id: platformId,
        profile_url: profileUrl || null,
        username: username || null,
        bio: bio || null,
        website_url: websiteUrl || null,
        notes: notes || null,
      });
      setStatus('Profile created!');
      setPlatformId(''); setProfileUrl(''); setUsername(''); setBio(''); setWebsiteUrl(''); setNotes('');
      await load();
      setActiveTab('profiles');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.patch(`/web20-profiles/${id}`, editData);
      setEditId(null);
      setEditData({});
      await load();
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/web20-profiles/${id}`);
      await load();
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setEditData({
      profile_url: p.profile_url || '',
      username: p.username || '',
      bio: p.bio || '',
      status: p.status || 'pending',
      posts_count: p.posts_count || 0,
      backlinks_live: p.backlinks_live || 0,
    });
  };

  const tabs = [
    { id: 'profiles', label: `Profiles (${profiles.length})` },
    { id: 'create', label: 'Add Profile' },
    { id: 'platforms', label: 'Platforms' },
  ];

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
          <Globe className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Web 2.0 Profile Management</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Track your high-DA web 2.0 profiles to build brand presence and off-page authority.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { label: 'Total', value: stats.total },
            { label: 'Live', value: stats.live },
            { label: 'Pending', value: stats.pending },
            { label: 'In Progress', value: stats.in_progress },
            { label: 'Inactive', value: stats.inactive || 0 },
            { label: 'Backlinks', value: stats.total_backlinks },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm text-center">
              <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium transition-colors ${
              activeTab === t.id
                ? 'border-b-2 border-violet-600 text-violet-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">

        {/* PROFILES TAB */}
        {activeTab === 'profiles' && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">{profiles.length} profiles tracked</p>
              <button type="button" onClick={load} className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {profiles.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Globe className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No profiles yet. Add your first Web 2.0 profile.</p>
              </div>
            ) : (
              profiles.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-3">
                  {editId === p.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Profile URL</label>
                          <input
                            type="url"
                            value={editData.profile_url || ''}
                            onChange={(e) => setEditData((d) => ({ ...d, profile_url: e.target.value }))}
                            className="w-full p-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Status</label>
                          <select
                            value={editData.status || 'pending'}
                            onChange={(e) => setEditData((d) => ({ ...d, status: e.target.value }))}
                            className="w-full p-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                          >
                            {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Posts Published</label>
                          <input
                            type="number"
                            value={editData.posts_count || 0}
                            onChange={(e) => setEditData((d) => ({ ...d, posts_count: parseInt(e.target.value) || 0 }))}
                            className="w-full p-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Live Backlinks</label>
                          <input
                            type="number"
                            value={editData.backlinks_live || 0}
                            onChange={(e) => setEditData((d) => ({ ...d, backlinks_live: parseInt(e.target.value) || 0 }))}
                            className="w-full p-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleUpdate(p.id)} className="bg-violet-600 text-white px-3 py-1 text-xs rounded hover:bg-violet-700 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Save
                        </button>
                        <button type="button" onClick={() => setEditId(null)} className="text-slate-500 text-xs hover:text-slate-700 px-3 py-1 border border-slate-200 rounded">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900 dark:text-white text-sm">{p.platform_name}</p>
                          <span className="text-xs text-slate-400">DA {p.platform_da}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[p.status] || STATUS_STYLES.pending}`}>
                            {p.status}
                          </span>
                        </div>
                        {p.username && <p className="text-xs text-slate-500">@{p.username}</p>}
                        {p.profile_url && (
                          <a href={p.profile_url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-violet-600 hover:underline flex items-center gap-1">
                            View Profile <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        <div className="flex gap-4 text-xs text-slate-500 pt-1">
                          <span>📝 {p.posts_count || 0} posts</span>
                          <span>🔗 {p.backlinks_live || 0} backlinks</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => startEdit(p)} className="text-slate-400 hover:text-violet-600 transition-colors">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <a href={p.platform_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-600">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button type="button" onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* CREATE TAB */}
        {activeTab === 'create' && (
          <form onSubmit={handleCreate} className="space-y-5 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Platform <span className="text-red-500">*</span>
              </label>
              <select
                value={platformId}
                onChange={(e) => setPlatformId(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              >
                <option value="">Select a platform</option>
                {platforms.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} (DA {p.da})</option>
                ))}
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Profile URL</label>
                <input
                  type="url"
                  value={profileUrl}
                  onChange={(e) => setProfileUrl(e.target.value)}
                  placeholder="https://wordpress.com/~yourusername"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="yourusername"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website URL (for backlink)</label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://yourclientsite.com"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bio / About</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Short bio for the profile..."
                rows={2}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Login credentials location, special requirements..."
                rows={2}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {loading ? 'Creating...' : 'Add Profile'}
            </button>
          </form>
        )}

        {/* PLATFORMS TAB */}
        {activeTab === 'platforms' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">Supported high-DA Web 2.0 platforms for off-page link building.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {platforms.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{p.name}</p>
                    <div className="flex gap-3 mt-1 text-xs text-slate-500">
                      <span>DA: <strong className="text-slate-700 dark:text-slate-300">{p.da}</strong></span>
                      <span className="capitalize">{p.category.replace('_', ' ')}</span>
                      {p.free && <span className="text-emerald-600 dark:text-emerald-400">Free</span>}
                    </div>
                  </div>
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium">
                    Visit <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            status.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'
          }`}>
            {status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
